import { useState } from "react"
import { Box, HStack, Text, VStack } from "@chakra-ui/react"
import { FiChevronDown, FiChevronRight } from "react-icons/fi"
import { AlignedTitles } from "@hydrologiq/shared/components/TitleWithChips"
import { AlertRuleCard } from "./AlertRuleCard"
import type { AlertRuleState } from "./AlertRuleCard"

interface SystemAlertGroupProps {
  systemRules: AlertRuleState[]
  systemId: string
  onEnable: (templateId: string) => void
  onDisable: (assignmentId: string) => void
  onUpdateThreshold: (assignmentId: string | undefined | null, value: number, templateId: string) => void
  onUpdateTimeWindow: (assignmentId: string | undefined | null, value: number, templateId: string) => void
  onReset: (assignmentId: string) => void | Promise<void>
}

export function SystemAlertGroup({
  systemRules,
  onEnable,
  onDisable,
  onUpdateThreshold,
  onUpdateTimeWindow,
  onReset,
}: SystemAlertGroupProps) {
  const [expanded, setExpanded] = useState(true)

  if (systemRules.length === 0) return null

  const activeCount = systemRules.filter((r) => r.isEnabled).length
  const totalCount = systemRules.length

  return (
    <Box borderWidth="1px" borderRadius="md" overflow="hidden">
      <HStack
        px={4}
        py={3}
        cursor="pointer"
        onClick={() => setExpanded(!expanded)}
        _hover={{ bg: "bg.subtle" }}
        justify="space-between"
      >
        <HStack gap={2}>
          {expanded ? <FiChevronDown /> : <FiChevronRight />}
          <Text fontSize="md" fontWeight="semibold">
            System
          </Text>
        </HStack>
        <Text fontSize="sm" color="fg.muted">
          {activeCount} of {totalCount} active
        </Text>
      </HStack>

      {expanded && (
        // Every card's chips line up after the longest name
        <AlignedTitles>
        <VStack gap={2} align="stretch" p={2}>
          {systemRules.map((rule) => (
            <AlertRuleCard
              key={rule.template.id}
              rule={rule}
              onToggle={(enabled) => {
                if (enabled) {
                  return onEnable(rule.template.id)
                } else if (rule.assignment) {
                  return onDisable(rule.assignment.id)
                }
              }}
              onUpdateThreshold={(value) => {
                return onUpdateThreshold(rule.assignment?.id, value, rule.template.id)
              }}
              onUpdateTimeWindow={(value) => {
                return onUpdateTimeWindow(rule.assignment?.id, value, rule.template.id)
              }}
              onReset={() => {
                if (rule.assignment) {
                  return onReset(rule.assignment.id)
                }
              }}
            />
          ))}
        </VStack>
        </AlignedTitles>
      )}
    </Box>
  )
}

export default SystemAlertGroup
